import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import logo from '../assets/logo.svg';
import { BrandBackdrop } from '../components/BrandBackdrop';
import { ACCEPT_INVITE_PATH } from '../hooks/useInviteContinuation';
import { safeContinuePath } from '../utils/safeContinuePath';
import { landAfterSignIn } from '../utils/signInLanding';

interface Workspace {
  id: string;
  name: string;
  role: string;
}

/** Above this many workspaces the list gets a search box. */
const SEARCH_THRESHOLD = 6;

const AVATAR_TONES = [
  'from-emerald-400 to-emerald-600',
  'from-sky-400 to-sky-600',
  'from-violet-400 to-violet-600',
  'from-amber-400 to-amber-600',
  'from-rose-400 to-rose-600',
  'from-teal-400 to-teal-600',
  'from-indigo-400 to-indigo-600',
];

function initialsOf(name: string): string {
  const words = name.split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

/** A stable tone per workspace, so the same workspace always looks the same. */
function toneOf(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return AVATAR_TONES[hash % AVATAR_TONES.length];
}

function roleBadgeClass(role: string): string {
  switch (role) {
    case 'owner':
      return 'bg-emerald-50 text-emerald-700 ring-emerald-600/20';
    case 'admin':
      return 'bg-sky-50 text-sky-700 ring-sky-600/20';
    default:
      return 'bg-gray-50 text-gray-600 ring-gray-500/20';
  }
}

/**
 * WorkspacePicker page for OIDC authorization code flow.
 * Displayed when a user belongs to multiple workspaces.
 * Allows workspace selection before continuing the OAuth flow.
 *
 * It sits on the LevelUp brand backdrop, like the workspace home, settings and
 * onboarding, so signing in to any suite app looks like the same product.
 *
 * Suite apps' staging E2E sign in through this page and pick a workspace by
 * its buttons, so the markup keeps three promises:
 *  - every workspace is a `<button>`, and they come first in the DOM — no
 *    other button precedes the list;
 *  - a workspace button's text STARTS with the workspace name (the avatar
 *    initials follow it in the DOM and are moved to the front with CSS order);
 *  - the role is the plain role word ("owner"), capitalised only by CSS.
 */
export function WorkspacePickerPage() {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [query, setQuery] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get the continue URL (encoded OAuth params)
  const continueUrl = searchParams.get('continue') || '';

  // NEVER STAND BETWEEN SOMEONE AND THEIR INVITATION.
  //
  // Whichever route sent them here, a continuation pointing at an invitation
  // makes this page a question with no bearing on the answer: the workspace is
  // the invitation's, and accepting binds the session to it, so a choice made
  // here is discarded moments later. Callers are expected to honour an
  // invitation before redirecting here — this is the backstop for the ones
  // that do not, and for any route added later that forgets.
  const invitePending = continueUrl.startsWith(ACCEPT_INVITE_PATH);

  useEffect(() => {
    if (invitePending) {
      navigate(continueUrl, { replace: true });
      return;
    }
    fetchWorkspaces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitePending]);

  const fetchWorkspaces = async () => {
    try {
      const response = await fetch('/api/oidc/session/workspaces', {
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 401) {
          // No session - redirect to login. Replace, not push: Back must not
          // return here only to bounce to sign-in again.
          const loginUrl = continueUrl
            ? `/auth/login?continue=${encodeURIComponent(continueUrl)}`
            : '/auth/login';
          navigate(loginUrl, { replace: true });
          return;
        }
        throw new Error('Failed to load workspaces');
      }

      const data = await response.json();
      setWorkspaces(data.workspaces || []);

      // Auto-select if only one workspace
      if (data.workspaces?.length === 1) {
        await selectWorkspace(data.workspaces[0].id);
      }
    } catch (err) {
      setError('Failed to load workspaces. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const selectWorkspace = async (workspaceId: string) => {
    setSelectedId(workspaceId);
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch('/api/oidc/session/select-workspace', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ workspaceId }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to select workspace');
      }

      // If this selection is part of an OAuth/OIDC flow, continueUrl holds
      // the full path to resume (e.g. /api/oidc/authorize?response_type=code&...).
      // Honor a real continue target exactly. A bare `/` is NOT a real
      // continue target — it is the admin control plane (403s for non-admins) —
      // so treat `/` (and an empty value) as "no continue": the sign-in
      // landing then sends a Central admin to the console and everyone else
      // to the Central /home route.
      //
      // C0: `continue` is caller input. It is followed only when the existing
      // validator accepts it (a same-origin path: no scheme, no '//' or '/\\',
      // no dot segments, raw or decoded), and it is the ORIGINAL validated
      // string that is followed. `javascript:`, `https://evil…`, `//evil…`
      // used to be assigned to location.href as-is (script on Central's
      // origin / open redirect). Anything unsafe is never followed: it counts
      // as no continue.
      const safeContinue = safeContinuePath(continueUrl);
      if (safeContinue && safeContinue !== '/') {
        window.location.href = safeContinue;
      } else {
        await landAfterSignIn(navigate);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to select workspace');
      setSelectedId(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const signInAgain = () => {
    const loginUrl = continueUrl
      ? `/auth/login?continue=${encodeURIComponent(continueUrl)}`
      : '/auth/login';
    navigate(loginUrl);
  };

  const showSearch = workspaces.length > SEARCH_THRESHOLD;
  const needle = query.trim().toLowerCase();
  const visible = showSearch && needle
    ? workspaces.filter(
        (w) => w.name.toLowerCase().includes(needle) || w.id.toLowerCase().startsWith(needle),
      )
    : workspaces;

  if (isLoading) {
    return (
      <BrandBackdrop>
        <PickerHeader />
        <main className="flex flex-1 items-start justify-center px-4 pb-10 pt-6 sm:pt-8">
          <div className="w-full max-w-lg rounded-3xl bg-white p-8 text-center shadow-2xl shadow-black/40 ring-1 ring-black/5">
            <div role="status" className="flex flex-col items-center">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent" />
              <p className="mt-4 text-gray-500">Loading your workspaces…</p>
            </div>
          </div>
        </main>
      </BrandBackdrop>
    );
  }

  return (
    <BrandBackdrop>
      <PickerHeader />
      <main className="flex flex-1 items-start justify-center px-4 pb-10 pt-6 sm:pt-8">
        <div className="w-full max-w-3xl">
          <div className="text-center">
            {workspaces.length > 0 && (
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-emerald-300/90">
                {workspaces.length} {workspaces.length === 1 ? 'workspace' : 'workspaces'}
              </p>
            )}
            <h1 className="mt-3 text-2xl font-semibold tracking-tight text-white sm:text-3xl">
              Choose your workspace
            </h1>
            <p className="mt-2 text-slate-300">Pick where you want to continue.</p>
          </div>

          <div className="mt-6 rounded-3xl bg-white p-3 shadow-2xl shadow-black/40 ring-1 ring-black/5 sm:p-4">
            {error && (
              <div role="alert" className="mx-1 mb-3 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700 ring-1 ring-red-200">
                {error}
              </div>
            )}

            {showSearch && (
              <div className="relative mx-1 mb-3">
                <svg
                  aria-hidden="true"
                  className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 100-15 7.5 7.5 0 000 15z" />
                </svg>
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search workspaces"
                  aria-label="Search workspaces"
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-10 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-emerald-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400/40"
                />
              </div>
            )}

            {visible.length > 0 && (
              <ul
                aria-label="Your workspaces"
                className="grid grid-cols-1 gap-2 p-1 sm:max-h-[max(16rem,calc(100vh-27rem))] sm:grid-cols-2 sm:overflow-y-auto"
              >
                {visible.map((workspace) => {
                  const isSelected = selectedId === workspace.id;
                  return (
                    <li key={workspace.id}>
                      <button
                        type="button"
                        onClick={() => selectWorkspace(workspace.id)}
                        disabled={isSubmitting}
                        aria-busy={isSelected && isSubmitting ? true : undefined}
                        className={`group flex h-full w-full items-center gap-3 rounded-2xl border p-3 text-left transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 ${
                          isSelected
                            ? 'border-emerald-400 bg-emerald-50/70 shadow-sm'
                            : 'border-gray-100 bg-white hover:-translate-y-0.5 hover:border-emerald-200 hover:shadow-md'
                        } disabled:cursor-not-allowed ${isSubmitting && !isSelected ? 'opacity-50' : ''}`}
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-semibold text-gray-900">{workspace.name}</span>
                          <span className="mt-1 flex items-center gap-2">
                            <span
                              className={`flex-none rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ring-1 ring-inset ${roleBadgeClass(
                                workspace.role,
                              )}`}
                            >
                              {workspace.role}
                            </span>
                            <span className="truncate font-mono text-xs text-gray-400">
                              {workspace.id.slice(0, 8)}…
                            </span>
                          </span>
                        </span>
                        <span
                          aria-hidden="true"
                          className={`order-first flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-gradient-to-br text-sm font-semibold text-white shadow-sm ${toneOf(
                            workspace.id,
                          )}`}
                        >
                          {initialsOf(workspace.name)}
                        </span>
                        <span aria-hidden="true" className="flex h-5 w-5 flex-none items-center justify-center">
                          {isSelected && isSubmitting ? (
                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent" />
                          ) : (
                            <svg
                              className="h-4 w-4 text-gray-300 transition-all group-hover:translate-x-0.5 group-hover:text-emerald-600"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            {workspaces.length > 0 && visible.length === 0 && (
              <p className="px-4 py-8 text-center text-sm text-gray-500">
                No workspaces match “{query.trim()}”.
              </p>
            )}

            {workspaces.length === 0 && (
              <div className="px-4 py-10 text-center">
                <p className="font-medium text-gray-900">No workspaces available.</p>
                <p className="mt-1 text-sm text-gray-500">Please contact your administrator.</p>
              </div>
            )}

            <div className="mx-1 mt-3 border-t border-gray-100 px-2 pb-1 pt-3">
              <button
                type="button"
                onClick={signInAgain}
                className="text-sm font-medium text-gray-500 transition-colors hover:text-emerald-700 focus:outline-none focus-visible:underline"
              >
                ← Sign in with a different account
              </button>
            </div>
          </div>
        </div>
      </main>
    </BrandBackdrop>
  );
}

function PickerHeader() {
  return (
    <header className="flex justify-center px-4 pt-8 sm:pt-12">
      <img src={logo} alt="LevelUp" className="h-7 drop-shadow sm:h-9" />
    </header>
  );
}
